/**
 * GeoMapPanel — agent-rendered Google Map with pins, optional connections,
 * and an optional time-color gradient. Drives:
 *   - The chat agent's `render_geo_map` tool (rendered via CenterPanelRenderer)
 *   - The TopicDetailPanel "Map" tab (deferred to a follow-up PR)
 *   - The upcoming Narrative-document feature (renders artifact JSON inline
 *     with `embedded={true}` to strip the outer Card chrome)
 *
 * Props:
 *   payload: {
 *     artifact_id, title, caption,
 *     source: { topic_id, transaction_ids? },
 *     pins: [{ id, place_id, label, sublabel?, kind, date?, transaction_id?, amount? }],
 *     connections: [{ id, kind, pin_ids[], label?, style }],
 *     time_gradient: { enabled, palette },
 *   }
 *   embedded?: boolean   — strip outer Card chrome (used by TopicDetailPanel /
 *                          Narrative-document hosts).
 *   onInteraction?: (event) => void
 *                          — bubbles { type: 'pin_click', transaction_id, pin_id }
 *                          so future drill-ins can route to TransactionAnalyzerPanel.
 *
 * Forensic invariants:
 *   - lat/lng comes ONLY from Place.fetchFields at render time. The payload
 *     carries place_id only — no cached coords on the wire.
 *   - Every pin renders a NUMBER badge (chronological 1..N). Time gradient is
 *     decoration; numbers carry the time signal independently for colorblind
 *     users.
 *   - place_ids that fail to resolve render a "N pin(s) could not be located"
 *     footer chip — never a silent forensic omission.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../design-system';
import { useGoogleMapsApi } from '../ui/googleMapsLoader';
import { resolvePlaceIdsToCoords } from '../ui/placeIdResolver';
import { formatCurrency } from '../../utils/formatters';
import styles from './GeoMapPanel.module.css';

// Design-token color values for the gradient palettes. Kept in sync with
// the backend validation in topic.py (VALID_GEO_GRADIENT_PALETTES) — when
// adding a palette here, mirror it there.
//
// Hex values rather than `var(--...)` because the Maps JS API consumes raw
// CSS color strings for marker DOM nodes and polyline strokeColor, and
// resolving CSS variables would require getComputedStyle() at render time.
// Tokens in colors.css are the source of truth; this map is a mirror.
const GRADIENT_PALETTES = {
  navy_to_teal: ['#1B2F5E', '#14b8a6'],     // navy-900 -> accent-500 (default)
  teal_to_navy: ['#14b8a6', '#1B2F5E'],
  cool_to_warm: ['#3b82f6', '#d97706'],     // info-500 -> warning-500
  warm_to_cool: ['#d97706', '#3b82f6'],
  green_to_red: ['#1d9b5e', '#dc2626'],     // success-500 -> error-500 (escalation)
  red_to_green: ['#dc2626', '#1d9b5e'],
};

const DEFAULT_PALETTE = 'navy_to_teal';
const FALLBACK_PIN_COLOR = '#1B2F5E'; // navy-900 — no-gradient case
const KIND_LABEL = {
  merchant: 'Merchant',
  trip_origin: 'Trip origin',
  trip_destination: 'Trip destination',
  trip_waypoint: 'Waypoint',
  context_anchor: 'Anchor',
};

// Linear-RGB interpolation between two hex strings (e.g. '#1B2F5E' and
// '#14b8a6'). Returns CSS hex. Used to spread gradient stops across the
// chronological pin order.
function interpolateHex(a, b, t) {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 0xff;
  const ag = (pa >> 8) & 0xff;
  const ab = pa & 0xff;
  const br = (pb >> 16) & 0xff;
  const bg = (pb >> 8) & 0xff;
  const bb = pb & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
}

function buildGradientColors(palette, count) {
  const [start, end] = GRADIENT_PALETTES[palette] || GRADIENT_PALETTES[DEFAULT_PALETTE];
  if (count <= 1) return [start];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push(interpolateHex(start, end, i / (count - 1)));
  }
  return out;
}

// Base transform that centers the badge on its lat/lng anchor.
// AdvancedMarkerElement anchors content bottom-center on the position, so we
// push it down by half its height to sit ON the point. The declutter pass
// (see below) prepends a pixel translate to this, so keep it as a shared
// constant referenced from both places.
const BASE_MARKER_TRANSFORM = 'translate(0, 50%)';

// Numbered pin DOM node — required by AdvancedMarkerElement, which positions
// content with its bottom-center anchored on the lat/lng. We translate the
// badge so the visual center sits on the position. `selected=true` grows
// the badge and outlines it so users can SEE which pin they just clicked
// (especially important when overlapping pins are fanned around the same
// underlying place_id).
function makeNumberedPin(number, color, selected) {
  const wrapper = document.createElement('div');
  const size = selected ? 36 : 28;
  const border = selected ? 3 : 2;
  const fontSize = selected ? 14 : 12;
  const shadow = selected
    ? '0 0 0 3px rgba(20, 184, 166, 0.35), 0 2px 6px rgba(16, 42, 67, 0.55)'
    : '0 1px 3px rgba(16, 42, 67, 0.45)';
  wrapper.style.cssText = `
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background: ${color};
    color: #FFFFFF;
    border: ${border}px solid #FFFFFF;
    box-shadow: ${shadow};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSize}px;
    font-weight: 600;
    font-family: system-ui, -apple-system, sans-serif;
    transform: ${BASE_MARKER_TRANSFORM};
    pointer-events: auto;
    cursor: pointer;
    transition: width 120ms ease, height 120ms ease, transform 150ms ease;
  `;
  wrapper.textContent = String(number);
  return wrapper;
}

// Pixel-space declutter. Markers stay anchored on their TRUE resolved
// coordinates; when two badges land within MIN_SEPARATION_PX of each other
// at the current zoom, we spiral the colliding group apart in SCREEN pixels
// so no two numbered badges overlap.
//
// Why pixels, not degrees: a fixed geographic offset (the previous approach)
// is sub-pixel at low zoom — badges still stack — and only separates at
// street-level zoom. Worse, it only fired for *identical* coordinates, so
// distinct-but-close pins (several merchants in one city) never separated at
// all. Screen-space collision detection recomputed on every zoom/pan keeps a
// constant on-screen gap at ALL zoom levels and covers both cases. Genuinely
// distant pins pull apart naturally as you zoom in and their offsets relax to
// zero.
const MIN_SEPARATION_PX = 30; // badge is 28px (36px selected)
const SPIRAL_STEP_PX = 34;    // neighbor spacing on the declutter spiral
// Golden angle — phyllotaxis spiral gives even spacing for any group size,
// with the first member at the center (offset 0) so tight singletons/pairs
// barely move.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

// records: [{ content, number, position: {lat,lng} }]; projection: a live
// MapCanvasProjection from an OverlayView added to the map. Mutates each
// record's content transform in place.
function declutterMarkers(records, projection) {
  if (!projection || !records || records.length === 0) return;
  const LatLng = window.google.maps.LatLng;
  const pts = records.map((r) => {
    const p = projection.fromLatLngToContainerPixel(
      new LatLng(r.position.lat, r.position.lng),
    );
    return p ? { x: p.x, y: p.y } : null;
  });

  // Union-find: connect any two badges whose centers are within MIN_SEPARATION.
  const n = records.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i) => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    while (parent[i] !== root) { const next = parent[i]; parent[i] = root; i = next; }
    return root;
  };
  const minSq = MIN_SEPARATION_PX * MIN_SEPARATION_PX;
  for (let i = 0; i < n; i += 1) {
    if (!pts[i]) continue;
    for (let j = i + 1; j < n; j += 1) {
      if (!pts[j]) continue;
      const dx = pts[i].x - pts[j].x;
      const dy = pts[i].y - pts[j].y;
      if (dx * dx + dy * dy < minSq) parent[find(i)] = find(j);
    }
  }

  const clusters = new Map();
  for (let i = 0; i < n; i += 1) {
    if (!pts[i]) continue;
    const root = find(i);
    const list = clusters.get(root) || [];
    list.push(i);
    clusters.set(root, list);
  }

  clusters.forEach((members) => {
    if (members.length === 1) {
      records[members[0]].content.style.transform = BASE_MARKER_TRANSFORM;
      return;
    }
    // Stable order: earliest chronological number sits at the spiral center.
    members.sort((a, b) => records[a].number - records[b].number);
    let cx = 0;
    let cy = 0;
    members.forEach((m) => { cx += pts[m].x; cy += pts[m].y; });
    cx /= members.length;
    cy /= members.length;
    members.forEach((m, i) => {
      const radius = SPIRAL_STEP_PX * Math.sqrt(i);
      const theta = i * GOLDEN_ANGLE;
      const dx = cx + radius * Math.cos(theta) - pts[m].x;
      const dy = cy + radius * Math.sin(theta) - pts[m].y;
      records[m].content.style.transform = `translate(${dx}px, ${dy}px) ${BASE_MARKER_TRANSFORM}`;
    });
  });
}

function sortPinsForNumbering(pins) {
  // Stable chronological sort: dated pins first by date asc, then by amount
  // desc (heavy hitters earlier), with undated pins at the end in original
  // order. Numbering follows this order.
  const dated = [];
  const undated = [];
  pins.forEach((p, idx) => {
    if (p.date) dated.push({ p, idx });
    else undated.push({ p, idx });
  });
  dated.sort((a, b) => {
    if (a.p.date < b.p.date) return -1;
    if (a.p.date > b.p.date) return 1;
    const aa = Math.abs(a.p.amount || 0);
    const bb = Math.abs(b.p.amount || 0);
    if (aa > bb) return -1;
    if (aa < bb) return 1;
    return a.idx - b.idx;
  });
  return [...dated.map((x) => x.p), ...undated.map((x) => x.p)];
}

function formatPinDate(date) {
  if (!date) return null;
  try {
    // Use Date.UTC to avoid tz off-by-one — payload dates are YYYY-MM-DD.
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return date;
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return date;
  }
}

function GeoMapPanel({ payload, onInteraction, embedded = false }) {
  const { isReady, mapId } = useGoogleMapsApi();
  const mapElRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]); // { marker, polyline } collected per redraw
  const projectionOverlayRef = useRef(null); // OverlayView -> pixel projection
  const markerRecordsRef = useRef([]); // { content, number, position } per pin
  const wrapperRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [selectedPinId, setSelectedPinId] = useState(null);

  const pins = useMemo(() => (payload && payload.pins) || [], [payload]);
  const connections = useMemo(
    () => (payload && payload.connections) || [],
    [payload],
  );

  // Build the chronological order ONCE per payload — numbering and gradient
  // are deterministic from the input pins.
  const orderedPins = useMemo(() => sortPinsForNumbering(pins), [pins]);
  // Map from pin.id -> { index (1-based), color }
  const pinDecorations = useMemo(() => {
    const gradient = payload?.time_gradient;
    const usingGradient = !!(gradient && gradient.enabled);
    const colors = usingGradient
      ? buildGradientColors(gradient.palette || DEFAULT_PALETTE, orderedPins.length)
      : null;
    const out = new Map();
    orderedPins.forEach((p, idx) => {
      out.set(p.id, {
        number: idx + 1,
        color: colors ? colors[idx] : FALLBACK_PIN_COLOR,
      });
    });
    return out;
  }, [orderedPins, payload]);

  // Resolve place_ids -> {lat,lng} via the shared module-scoped cache.
  // Re-run when the pin set changes OR the Maps API becomes ready.
  useEffect(() => {
    if (!isReady || !pins.length) {
      setResolved(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const coords = await resolvePlaceIdsToCoords(pins.map((p) => p.place_id));
      if (cancelled) return;
      setResolved(coords);
    })();
    return () => { cancelled = true; };
  }, [isReady, pins]);

  // Markers are placed on their TRUE resolved coords; overlapping badges are
  // separated in screen space by declutterMarkers (recomputed on zoom/pan),
  // not by nudging the underlying coordinate.
  const displayCoords = resolved;

  // pin.id -> displayed {lat, lng}; used by the pin-list-below-map "click
  // to locate" handler so it doesn't need to re-run the resolve effect.
  const pinIdToCoords = useMemo(() => {
    const out = new Map();
    if (!displayCoords) return out;
    pins.forEach((p, idx) => {
      if (displayCoords[idx]) out.set(p.id, displayCoords[idx]);
    });
    return out;
  }, [pins, displayCoords]);

  // Click a row in the pin list -> pan + zoom the map to that pin AND
  // mark it selected so the marker grows + outlines (visible feedback even
  // when stacked under sibling pins). Holds the existing zoom if it's
  // already deep enough so the user's manual zoom isn't yanked back to a
  // fixed level.
  const handlePinListClick = useCallback((pin) => {
    const coords = pinIdToCoords.get(pin.id);
    const map = mapInstanceRef.current;
    if (!coords || !map) return;
    setSelectedPinId(pin.id);
    map.panTo(coords);
    const current = map.getZoom() || 0;
    if (current < 15) map.setZoom(15);
  }, [pinIdToCoords]);

  // Initialize the map and draw markers + connections.
  useEffect(() => {
    if (!isReady || !mapElRef.current || !displayCoords) return undefined;
    if (!window.google || !window.google.maps) return undefined;

    // Collect resolved markers in pin-id order so we can hand them to
    // AdvancedMarkerElement and to the polyline builder. Uses the fanned-
    // out display coords so overlapping pins don't stack invisibly.
    const pinCoords = pins.map((p, idx) => ({ pin: p, coords: displayCoords[idx] }));
    const validPinCoords = pinCoords.filter((x) => x.coords);
    if (validPinCoords.length === 0) {
      // All pins failed to resolve — render nothing on the map; footer
      // chip already reports the count.
      return undefined;
    }

    // Compute bounds. Single-pin case: pan to it at zoom 14.
    const bounds = new window.google.maps.LatLngBounds();
    validPinCoords.forEach((x) => bounds.extend(x.coords));
    const center = bounds.getCenter();

    // Init or reuse the map instance.
    let map = mapInstanceRef.current;
    if (!map) {
      map = new window.google.maps.Map(mapElRef.current, {
        mapId,
        center,
        zoom: 4,
        disableDefaultUI: false,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
      mapInstanceRef.current = map;
    } else {
      map.setCenter(center);
    }

    // Tear down previous overlays before redrawing.
    overlaysRef.current.forEach((ov) => {
      if (ov.marker) ov.marker.map = null;
      if (ov.polyline) ov.polyline.setMap(null);
    });
    overlaysRef.current = [];

    // Fit bounds — but skip if only one point (the manual setZoom does
    // the right thing).
    if (validPinCoords.length === 1) {
      map.setCenter(validPinCoords[0].coords);
      map.setZoom(14);
    } else {
      map.fitBounds(bounds, 64);
    }

    // Draw markers.
    const localPinIdToCoords = new Map();
    const markerRecords = [];
    validPinCoords.forEach(({ pin, coords }) => {
      localPinIdToCoords.set(pin.id, coords);
      const decor = pinDecorations.get(pin.id) || { number: '?', color: FALLBACK_PIN_COLOR };
      const isSelected = pin.id === selectedPinId;
      const content = makeNumberedPin(decor.number, decor.color, isSelected);
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: coords,
        map,
        title: pin.label || '',
        content,
        gmpClickable: true,
        zIndex: isSelected ? 9999 : decor.number,
      });
      // Numeric badge number for stable spiral ordering during declutter.
      // Non-numeric fallback ('?') sorts last.
      markerRecords.push({
        content,
        number: typeof decor.number === 'number' ? decor.number : Number.MAX_SAFE_INTEGER,
        position: coords,
      });
      const onEnter = (evt) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        const x = rect ? (evt.clientX - rect.left) : evt.clientX;
        const y = rect ? (evt.clientY - rect.top) : evt.clientY;
        setHoverInfo({ pin, decor, x, y });
      };
      const onLeave = () => setHoverInfo(null);
      content.addEventListener('mouseenter', onEnter);
      content.addEventListener('mouseleave', onLeave);
      content.addEventListener('mousemove', onEnter);
      content.addEventListener('click', () => {
        // Clicking a marker also marks it selected — keeps the list row
        // highlight in sync with map interaction.
        setSelectedPinId(pin.id);
        if (onInteraction && pin.transaction_id) {
          onInteraction({
            type: 'pin_click',
            transaction_id: pin.transaction_id,
            pin_id: pin.id,
          });
        }
      });
      overlaysRef.current.push({ marker });
    });

    // Draw connections — anchored on the pins' true resolved coords (the
    // declutter pass only nudges the badge DOM, not the underlying point).
    connections.forEach((conn) => {
      const pathCoords = conn.pin_ids
        .map((pid) => localPinIdToCoords.get(pid))
        .filter((c) => !!c);
      if (pathCoords.length < 2) return; // can't draw a polyline with <2 valid points
      const isDashed = (conn.style || (conn.kind === 'trip' ? 'solid' : 'dashed')) === 'dashed';
      const strokeColor = conn.kind === 'trip' ? '#0891b2' : '#486581'; // accent-600 / navy-600
      const polyline = new window.google.maps.Polyline({
        path: pathCoords,
        geodesic: true,
        strokeColor,
        strokeOpacity: isDashed ? 0 : 0.85,
        strokeWeight: 2,
        map,
        icons: isDashed
          ? [{
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor,
              scale: 3,
            },
            offset: '0',
            repeat: '12px',
          }]
          : (conn.kind === 'trip'
            ? [{
              icon: {
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                fillColor: strokeColor,
                fillOpacity: 1,
                strokeColor,
                strokeWeight: 2,
              },
              offset: '100%',
            }]
            : undefined),
      });
      overlaysRef.current.push({ polyline });
    });

    // Publish the fresh records for the declutter pass.
    markerRecordsRef.current = markerRecords;

    // A hidden OverlayView gives us a live lat/lng -> container-pixel
    // projection so the declutter pass can measure on-screen collisions.
    // Created once; reused across redraws.
    if (!projectionOverlayRef.current) {
      const overlay = new window.google.maps.OverlayView();
      overlay.onAdd = () => {};
      overlay.draw = () => {};
      overlay.onRemove = () => {};
      overlay.setMap(map);
      projectionOverlayRef.current = overlay;
    }

    const runDeclutter = () => {
      const projection = projectionOverlayRef.current?.getProjection();
      declutterMarkers(markerRecordsRef.current, projection);
    };

    // Re-declutter whenever the view settles (covers zoom AND pan). `idle`
    // fires after fitBounds/panTo complete, by which point the projection is
    // ready. An initial rAF handles the case where markers were rebuilt but
    // the view didn't move (e.g. a selection change).
    const idleListener = map.addListener('idle', runDeclutter);
    const rafId = window.requestAnimationFrame(runDeclutter);

    return () => {
      // Markers/overlays are torn down at the top of the next run; only the
      // per-run listeners need removing here so they don't stack.
      if (idleListener) idleListener.remove();
      window.cancelAnimationFrame(rafId);
    };
  }, [isReady, mapId, displayCoords, pins, connections, pinDecorations, selectedPinId, onInteraction]);

  // ResizeObserver: when the panel resizes, recenter without re-init.
  useEffect(() => {
    if (!wrapperRef.current || !mapInstanceRef.current) return undefined;
    const ro = new ResizeObserver(() => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const center = map.getCenter();
      if (center) map.panTo(center);
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [displayCoords]);

  // Bail-out cases ----------------------------------------------------------
  if (!payload) {
    return (
      <div className={`${styles.panel} ${embedded ? styles.embedded : ''}`}>
        <div className={styles.emptyState}>No geo-map payload provided.</div>
      </div>
    );
  }

  if (!pins.length) {
    return (
      <div className={`${styles.panel} ${embedded ? styles.embedded : ''}`}>
        <div className={styles.emptyState}>
          No mappable locations for this topic. The agent will tell you in chat
          when a topic has no geocoded transactions.
        </div>
      </div>
    );
  }

  // Counts for the footer / legend ------------------------------------------
  const unresolvedCount = resolved
    ? resolved.filter((c) => !c).length
    : 0;
  const hasGradient = payload.time_gradient?.enabled;
  const palette = payload.time_gradient?.palette || DEFAULT_PALETTE;
  const [gStart, gEnd] = GRADIENT_PALETTES[palette] || GRADIENT_PALETTES[DEFAULT_PALETTE];
  const datedOrdered = orderedPins.filter((p) => p.date);
  const firstDate = datedOrdered[0]?.date;
  const lastDate = datedOrdered[datedOrdered.length - 1]?.date;

  const body = (
    <>
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{payload.title || 'Map'}</h2>
        </div>
      </div>
      {payload.caption ? (
        <blockquote className={styles.caption}>{payload.caption}</blockquote>
      ) : null}

      <div className={styles.mapShell} ref={wrapperRef}>
        <div ref={mapElRef} className={styles.mapEl} aria-label="Topic geo map" />
        {!isReady ? (
          <div className={styles.mapPlaceholder}>Loading map…</div>
        ) : null}
        {hoverInfo ? (
          <div
            className={styles.hoverCard}
            style={{
              left: Math.min(hoverInfo.x + 12, (wrapperRef.current?.clientWidth || 600) - 280),
              top: Math.max(hoverInfo.y - 60, 8),
            }}
          >
            <div className={styles.hoverLabel}>
              {hoverInfo.decor.number}. {hoverInfo.pin.label}
            </div>
            {hoverInfo.pin.sublabel ? (
              <div className={styles.hoverSublabel}>{hoverInfo.pin.sublabel}</div>
            ) : null}
            <div className={styles.hoverMeta}>
              <div className={styles.hoverMetaItem}>
                <div className={styles.hoverMetaLabel}>Kind</div>
                <div className={styles.hoverMetaValue}>
                  {KIND_LABEL[hoverInfo.pin.kind] || hoverInfo.pin.kind}
                </div>
              </div>
              {hoverInfo.pin.date ? (
                <div className={styles.hoverMetaItem}>
                  <div className={styles.hoverMetaLabel}>Date</div>
                  <div className={styles.hoverMetaValue}>{formatPinDate(hoverInfo.pin.date)}</div>
                </div>
              ) : null}
              {typeof hoverInfo.pin.amount === 'number' ? (
                <div className={styles.hoverMetaItem}>
                  <div className={styles.hoverMetaLabel}>Amount</div>
                  <div className={styles.hoverMetaValue}>{formatCurrency(hoverInfo.pin.amount)}</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {(hasGradient || connections.length > 0) ? (
        <div className={styles.legend}>
          {hasGradient ? (
            <div className={styles.legendGroup}>
              <span className={styles.legendLabel}>Time</span>
              <span className={styles.gradientEndpoints}>
                {firstDate ? (
                  <span className={styles.gradientDate}>{formatPinDate(firstDate)}</span>
                ) : null}
                <span
                  className={styles.gradientBar}
                  style={{ background: `linear-gradient(90deg, ${gStart}, ${gEnd})` }}
                />
                {lastDate && lastDate !== firstDate ? (
                  <span className={styles.gradientDate}>{formatPinDate(lastDate)}</span>
                ) : null}
              </span>
            </div>
          ) : null}
          {connections.length > 0 ? (
            <div className={styles.connectionsList}>
              {connections.map((conn) => {
                const isTrip = conn.kind === 'trip';
                return (
                  <span key={conn.id} className={styles.connectionChip}>
                    <span
                      className={`${styles.connectionChipSwatch} ${isTrip ? styles.connectionChipSwatchTrip : styles.connectionChipSwatchOther}`}
                    />
                    {conn.label || (isTrip ? 'Trip' : conn.kind)}
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.pinList} role="list" aria-label="Map pins">
        {orderedPins.map((pin) => {
          const decor = pinDecorations.get(pin.id);
          const hasCoords = pinIdToCoords.has(pin.id);
          const isSelected = pin.id === selectedPinId;
          return (
            <button
              type="button"
              key={pin.id}
              role="listitem"
              className={`${styles.pinListItem} ${isSelected ? styles.pinListItemSelected : ''}`}
              onClick={() => handlePinListClick(pin)}
              disabled={!hasCoords}
              aria-current={isSelected ? 'true' : undefined}
              title={hasCoords ? 'Show on map' : 'Could not be located on the map'}
            >
              <span
                className={styles.pinListBadge}
                style={{ background: decor?.color || FALLBACK_PIN_COLOR }}
                aria-hidden="true"
              >
                {decor?.number ?? '?'}
              </span>
              <span className={styles.pinListBody}>
                <span className={styles.pinListLabel}>{pin.label}</span>
                {pin.sublabel ? (
                  <span className={styles.pinListSublabel}>{pin.sublabel}</span>
                ) : null}
                <span className={styles.pinListMeta}>
                  <span>{KIND_LABEL[pin.kind] || pin.kind}</span>
                  {pin.date ? <span>{formatPinDate(pin.date)}</span> : null}
                  {typeof pin.amount === 'number' ? (
                    <span>{formatCurrency(pin.amount)}</span>
                  ) : null}
                  {!hasCoords ? (
                    <span className={styles.pinListUnlocated}>not located</span>
                  ) : null}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {unresolvedCount > 0 ? (
        <div className={styles.footerNote}>
          {unresolvedCount} pin{unresolvedCount === 1 ? '' : 's'} could not be
          located. Their place_id either expired or is not a Google place id.
          The narrative caption still applies — the locations exist; we just
          can&apos;t draw them on this map.
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div className={`${styles.panel} ${styles.embedded}`}>{body}</div>
    );
  }

  return (
    <div className={styles.panel}>
      <Card padding="lg">{body}</Card>
    </div>
  );
}

export default GeoMapPanel;
